import { useQuery } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';
import type { SyncLagLevel, SyncLagSnapshot } from './use-terminals';

/**
 * Kitchen station oversight (FN-91…93, ADR-0013). BOS is a mirror of the
 * station: these hooks read what the station uploaded — broker/printer
 * health inside sync telemetry, and every order transition — and never
 * send a command back.
 */

export type KitchenBrokerHealth = {
  configured: boolean;
  connected: boolean;
  consumerAlive: boolean;
  outboxDepth: number;
  queueName: string | null;
  lastMessageAt: string | null;
  lastError: string | null;
};

export type KitchenPrinterHealth = {
  configured: boolean;
  ready: boolean;
  model: string | null;
  target: string | null;
  lastError: string | null;
  lastPrintAt: string | null;
};

export type SyncLagKitchenState = {
  level: SyncLagLevel;
  reason: string | null;
  broker: KitchenBrokerHealth;
  printer: KitchenPrinterHealth;
  activeOrders: number | null;
};

export type KitchenOrderStatus =
  | 'queued'
  | 'preparing'
  | 'ready'
  | 'collected'
  | 'cancelled'
  | 'noShow';

export type KitchenOrderLine = {
  productId: number;
  name: string;
  quantity: number;
  unitPriceMinor: number;
};

export type KitchenOrderSummary = {
  id: string;
  terminalId: string;
  terminalCode: string | null;
  locationId: string | null;
  storeId: number;
  orderId: string;
  orderNumber: string;
  displayNumber: string;
  source: 'app' | 'station';
  status: KitchenOrderStatus;
  paymentState: 'notRequired' | 'paid' | 'hold' | 'failed';
  providerStatus: string | null;
  customerLabel: string | null;
  totalMinor: number;
  currency: string;
  pickupAt: string;
  createdAt: string;
  updatedAt: string;
  stickersPrinted: number;
  lastError: string | null;
  lines: KitchenOrderLine[];
};

export type KitchenOrderEventView = {
  id: string;
  fromStatus: KitchenOrderStatus | null;
  toStatus: KitchenOrderStatus;
  occurredAt: string;
  actor: string;
  note: string | null;
  receivedAt: string;
};

export type ListKitchenOrdersResponse = { orders: KitchenOrderSummary[]; total: number };

const KITCHEN_KEY = ['kitchen'] as const;

/** One terminal's sync-lag snapshot, with the kitchen block when reported. */
export function useSyncLag(terminalId: string | undefined) {
  return useQuery({
    queryKey: ['terminal-management', 'sync-lag', terminalId ?? 'none'] as const,
    enabled: terminalId !== undefined,
    queryFn: async (): Promise<SyncLagSnapshot & { kitchen?: SyncLagKitchenState }> => {
      const res = await apiClient.get<SyncLagSnapshot & { kitchen?: SyncLagKitchenState }>(
        `/terminals/${terminalId ?? ''}/sync-lag`,
      );
      return res.data;
    },
    refetchInterval: 30_000,
  });
}

export type KitchenOrdersFilter = {
  locationId?: string;
  terminalId?: string;
  status?: KitchenOrderStatus;
  page?: number;
  pageSize?: number;
};

export function useKitchenOrders(filter: KitchenOrdersFilter) {
  return useQuery({
    queryKey: [...KITCHEN_KEY, 'orders', filter] as const,
    queryFn: async (): Promise<ListKitchenOrdersResponse> => {
      const params: Record<string, string | number> = {};
      if (filter.locationId) params.locationId = filter.locationId;
      if (filter.terminalId) params.terminalId = filter.terminalId;
      if (filter.status) params.status = filter.status;
      params.page = filter.page ?? 1;
      params.pageSize = filter.pageSize ?? 50;
      const res = await apiClient.get<ListKitchenOrdersResponse>('/kitchen/orders', { params });
      return res.data;
    },
    refetchInterval: 15_000,
  });
}

export function useKitchenOrder(id: string | null) {
  return useQuery({
    queryKey: [...KITCHEN_KEY, 'order', id ?? 'none'] as const,
    enabled: id !== null,
    queryFn: async (): Promise<{ order: KitchenOrderSummary; events: KitchenOrderEventView[] }> => {
      const res = await apiClient.get<{
        order: KitchenOrderSummary;
        events: KitchenOrderEventView[];
      }>(`/kitchen/orders/${id ?? ''}`);
      return res.data;
    },
  });
}

export function formatMinor(minor: number, currency: string): string {
  const sign = minor < 0 ? '-' : '';
  const abs = Math.abs(minor);
  return `${sign}${Math.floor(abs / 100)}.${String(abs % 100).padStart(2, '0')} ${currency}`;
}
